'use client';

import React, { useMemo, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import DomeGallery from '../../components/DomeGallery/DomeGallery';
import avatecLogo from '../../assets/logos/avatec-white.png';
import './Slide08Partners.css';

interface Partner {
  name: string;
  logo: string;
}

const partners: Partner[] = [
  { name: 'Sperry', logo: '/images/partners/sperrymarine_new.svg' }, // 1
  { name: 'Furuno', logo: '/images/partners/furuno.png' }, // 2
  { name: 'Danalec', logo: '/images/partners/danelec.svg' }, // 3
  { name: 'ANSCHUTZ', logo: '/images/partners/anschutz.svg' }, // 4
  { name: 'JOTRON', logo: '/images/partners/jotron.png' }, // 5
  { name: 'SAL NAVIGATION', logo: '/images/logos/vdr/sal.png' }, // 6
  { name: 'JRC', logo: '/images/partners/jrc.png' }, // 6
  { name: 'SAL', logo: '/images/logos/vdr/sal.png' }, // 6
  { name: 'KANGROM', logo: '/images/partners/kangrim.svg' }, // 7
  { name: 'NACOS', logo: '/images/partners/nacos.png' }, // 8
  { name: 'LARS THRANE', logo: '/images/partners/Lars-Thrane.png' }, // 9
  { name: 'TOKYO KEIKI', logo: '/images/partners/Tokyo_Keiki.png' }, // 10
  { name: 'SAILOR COBHAM', logo: '/images/partners/Sailor_Cobham.webp' }, // 11
  { name: 'INTELLIAN', logo: '/images/partners/intellian.png' }, // 13
  { name: 'SIMRAD', logo: '/images/partners/simrad-navico.svg' }, // 14
  { name: 'AMI MARINE', logo: '/images/partners/ami-marine.png' }, // 15
  { name: 'WARTSILA', logo: '/images/partners/wartsila.png' }, // 16
  { name: 'MACGRAGOR', logo: '/images/partners/macgregor.svg' }, // 17
  { name: 'COMROD', logo: '/images/partners/comrod.png' }, // 18
  { name: 'MCMURDO', logo: '/images/partners/McMurdo-Group-Logo-FI.jpg' }, // 19
  { name: 'SCNN ANTENNA', logo: '/images/partners/scnn-antenna.png' }, // 20
  { name: 'HIGHLANDER', logo: '/images/partners/highlander.png' }, // 21
  { name: 'SEAS OF SOLUTION', logo: '/images/partners/seasofsolutions.svg' }, // 22
  { name: 'SAAB', logo: '/images/partners/Saab_Technologies_logo.svg' }, // 23
  { name: 'INMARSAT', logo: '/images/partners/inmarsat.svg' }, // 24
  { name: 'GILL', logo: '/images/partners/gill.svg' }, // 25
  { name: 'NHE', logo: '/images/partners/NIPPON_HAKUYO.png' }, // 26
  { name: 'SAFETEC', logo: '/images/partners/safetec.png' }, // 27
  { name: 'HYUNDAI', logo: '/images/partners/hyundai.png' }, // 28
  { name: 'MUSASINO', logo: '/images/partners/musasino.png' }, // 29
  { name: 'DEIF', logo: '/images/partners/deif.svg' }, // 30
  { name: 'SONIHULL', logo: '/images/partners/sonihull2-logo.jpg' }, // 31
  { name: 'WABTEC', logo: '/images/partners/Wabtec_Logo.png' }, // 32
  { name: 'ACR', logo: '/images/partners/acr.png' }, // 33
  { name: 'C-MAP', logo: '/images/partners/c-map.webp' }, // 34
  { name: 'COBHAM SAILOR ALT', logo: '/images/partners/cobham-sailor.png' }, // 35
  { name: 'CONSILIUM', logo: '/images/partners/consilium.svg' }, // 36
  { name: 'JMC', logo: '/images/partners/jmc.png' }, // 37
  { name: 'OCEAN SIGNAL', logo: '/images/partners/ocean-signal.svg' }, // 38
  { name: 'YOKOGAWA', logo: '/images/partners/yokogawa.png' }, // 40
];

/** Generate an SVG data-URI tile with the partner name (fallback for missing logos) */
function partnerToSvgUri(name: string): string {
  const w = 320;
  const h = 200;
  const escapedName = name
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const fontSize = name.length > 14 ? 16 : name.length > 10 ? 19 : 22;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#0c1a2e"/>
        <stop offset="100%" stop-color="#0a1220"/>
      </linearGradient>
    </defs>
    <rect width="${w}" height="${h}" rx="16" fill="url(#bg)"/>
    <rect x="1" y="1" width="${w - 2}" height="${h - 2}" rx="15" fill="none" stroke="rgba(100,180,255,0.15)" stroke-width="1"/>
    <line x1="40" y1="${h / 2 + 22}" x2="${w - 40}" y2="${h / 2 + 22}" stroke="rgba(100,180,255,0.12)" stroke-width="1"/>
    <text x="${w / 2}" y="${h / 2 + 6}" text-anchor="middle" dominant-baseline="central" font-family="Segoe UI,system-ui,sans-serif" font-weight="600" font-size="${fontSize}" fill="rgba(210,230,255,0.92)" letter-spacing="1.5">${escapedName}</text>
    <text x="${w / 2}" y="${h / 2 + 40}" text-anchor="middle" font-family="Segoe UI,system-ui,sans-serif" font-size="10" fill="rgba(140,180,220,0.45)" letter-spacing="2">PARTNER</text>
  </svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export default function Slide08PartnersLast() {
  const reduceMotion = useReducedMotion();
  const galleryRef = useRef<HTMLDivElement | null>(null);

  const totalPartners = partners.length;
  const splitIndex = Math.min(20, totalPartners);
  const visiblePartners = useMemo(
    () => partners.slice(splitIndex),
    [splitIndex]
  );

  const images = useMemo(
    () =>
      visiblePartners.map((p) => ({
        src: p.logo || partnerToSvgUri(p.name),
        alt: p.name,
      })),
    [visiblePartners]
  );

  return (
    <motion.div
      className="slide partners-white-cards relative flex h-screen w-full overflow-hidden bg-[#050d18] text-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.8, ease: 'easeOut' }}
    >
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />

      {/* Title */}
      <div className="pointer-events-none absolute left-[6%] top-[8%] z-10 text-2xl font-semibold tracking-wide text-white/85 sm:text-3xl md:text-4xl">
        Partners
      </div>

      {/* DomeGallery */}
      <div className="absolute inset-0" ref={galleryRef}>
        <AnimatePresence mode="wait">
          <motion.div
            key="last"
            className="h-full w-full"
            initial={{ opacity: 0, y: reduceMotion ? 0 : 26 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduceMotion ? 0 : -26 }}
            transition={{ duration: reduceMotion ? 0 : 0.45, ease: 'easeOut' }}
          >
            <DomeGallery
              images={images}
              overlayBlurColor="#050d18"
              grayscale={false}
              imageBorderRadius="14px"
              openedImageBorderRadius="18px"
              openedImageWidth="320px"
              openedImageHeight="200px"
              fit={0.55}
              segments={35}
              dragDampening={2}
              dragSensitivity={18}
              layout="flat"
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
